import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {AppState} from "lib/redux/store";
import {menuApi} from "lib/api/wineMenuApi";
import {Menu} from "@sobr/wine-menu-client";
import {Language} from "../i18n/translations";
import { MenuDocument } from "features/editor/MenuDocumentModel";
import { createDocumentTypeHandler } from "features/editor/documentHandlerUtils";
import { LabelTemplate } from "features/label-editor/LabelDocumentModel";

interface CreateMenuState {
    menuName: string
    menuNameError: string | null
    language: Language
    error: string | null
    loading: boolean
    successFullyCreatedMenu: Menu | null
}

const initialState: CreateMenuState = {
    menuName: "",
    menuNameError: null,
    language: "de",
    error: null,
    loading: false,
    successFullyCreatedMenu: null
};

export interface CreateMenuRequest {
    userName: string,
    menuName: string,
    language: Language,
    type: MenuDocument['type'];
    args?: {
        template?: LabelTemplate;
    }
}

const createMenu = createAsyncThunk<Menu, CreateMenuRequest, {}>(
    'menus/create',
    async (createMenuRequest: CreateMenuRequest) => {
        const handler = createDocumentTypeHandler(createMenuRequest.type);
        const response = await menuApi.createMenuForUser({
            userId: createMenuRequest.userName,
            name: createMenuRequest.menuName,
            body: handler.createEmptyDocument(createMenuRequest)
        });
        return response.data;
    }
);

const {actions, reducer} = createSlice({
    name: 'createMenu',
    initialState: initialState,
    reducers: {
        setName: (state, action: PayloadAction<string>) => {
            state.menuName = action.payload;
        },
        setNameError: (state, action: PayloadAction<CreateMenuState['menuNameError']>) => {
            state.menuNameError = action.payload;
        },
        setLanguage: (state, action: PayloadAction<Language>) => {
            state.language = action.payload;
        },
        reset: (state) => {
            state.menuName = "";
            state.menuNameError = null;
            state.error = null;
            state.loading = false;
            state.successFullyCreatedMenu = null;
        }
    },
    extraReducers: builder => {
        builder.addCase(createMenu.pending, (state: CreateMenuState) => {
            state.loading = true;
            state.menuNameError = null;
            state.error = null;
        });
        builder.addCase(createMenu.fulfilled, (state: CreateMenuState, action) => {
            state.menuName = "";
            state.menuNameError = null;
            state.error = null;
            state.loading = false;
            state.successFullyCreatedMenu = action.payload
        });
        builder.addCase(createMenu.rejected, (state: CreateMenuState, action) => {
            state.error = action.error.message ?? null;
            state.loading = false;
        });
    }
});

function getCreateMenuState(state: AppState): CreateMenuState {
    return state.menu.create;
}

const selectors = {
    menuName: (state: AppState) => getCreateMenuState(state).menuName,
    menuNameError: (state: AppState) => getCreateMenuState(state).menuNameError,
    language: (state: AppState) => getCreateMenuState(state).language,
    error: (state: AppState) => getCreateMenuState(state).error,
    loading: (state: AppState) => getCreateMenuState(state).loading,
    successfullyCreatedMenu: (state: AppState) => getCreateMenuState(state).successFullyCreatedMenu
}

const publicActions = {
    setName: actions.setName,
    setNameError: actions.setNameError,
    setLanguage: actions.setLanguage,
    createMenu: createMenu,
    reset: actions.reset
}

export {reducer as createMenuReducer};
export {publicActions as createMenuActions};
export {selectors as createMenuSelectors};